/**
 * SprintSpeak — Regulamin i Polityka prywatności
 * Wersja 1.0, obowiązuje od 10 sierpnia 2026 r.
 *
 * Plik jest samodzielnym modułem ES. Przykład użycia:
 * import { TERMS, PRIVACY } from './sprintspeak-regulamin-polityka-final.js';
 */

export const LEGAL_META = {
  appName: 'SprintSpeak',
  version: '1.0.1',
  effectiveDate: '10 sierpnia 2026 r.',
  lastUpdated: '10 sierpnia 2026 r.',
  contactEmail: 'hello@sprintspeak.pl',
  operator: {
    name: 'Wirebase Sp. z o.o.',
    address: 'Aleja Jana Pawła II 27, 00-867 Warszawa',
    krs: '0000868493',
    nip: '5272940820',
    regon: '387522572',
  },
  urls: {
    privacy: 'https://sprintspeak.pl/polityka-prywatnosci',
    terms: 'https://sprintspeak.pl/regulamin',
    deleteAccount: 'https://sprintspeak.pl/usun-konto',
    contact: 'https://sprintspeak.pl/kontakt',
  },
};

const operatorName = () =>
  `${LEGAL_META.operator.name} z siedzibą w Warszawie, ${LEGAL_META.operator.address}, ` +
  `wpisana do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS ` +
  `${LEGAL_META.operator.krs}, NIP ${LEGAL_META.operator.nip}, REGON ${LEGAL_META.operator.regon}`;

const mail = () => LEGAL_META.contactEmail;

/** Regulamin świadczenia usług drogą elektroniczną. */
export const TERMS = {
  title: 'Regulamin',
  lead: 'Zasady korzystania z aplikacji SprintSpeak — darmowych lekcji języka angielskiego dla branży IT.',
  version: LEGAL_META.version,
  effectiveDate: LEGAL_META.effectiveDate,
  lastUpdated: LEGAL_META.lastUpdated,
  sections: [
    {
      title: 'Postanowienia ogólne',
      paragraphs: [
        `Regulamin określa zasady świadczenia drogą elektroniczną usług dostępnych w aplikacji ${LEGAL_META.appName}, w jej wersji mobilnej oraz przeglądarkowej PWA („Aplikacja”).`,
        `Usługodawcą, administratorem Aplikacji i właścicielem udostępnianych w niej materiałów jest ${operatorName()} („Usługodawca”).`,
        `Kontakt z Usługodawcą jest możliwy pod adresem ${mail()} oraz przez stronę ${LEGAL_META.urls.contact}.`,
        `Aktualny Regulamin jest dostępny nieodpłatnie w Aplikacji oraz pod adresem ${LEGAL_META.urls.terms}, w sposób umożliwiający jego wyświetlenie, zapisanie i odtworzenie.`,
        'Aplikacja jest przeznaczona dla konsumentów indywidualnych korzystających z usługi na terytorium Polski.',
      ],
    },
    {
      title: 'Użytkownik i zawarcie umowy',
      paragraphs: [
        'Użytkownikiem może być wyłącznie osoba fizyczna, która ukończyła 18 lat i korzysta z Aplikacji w celach niezwiązanych bezpośrednio ze swoją działalnością gospodarczą lub zawodową.',
        'Aplikacja nie prowadzi technicznej weryfikacji wieku użytkownika. Osoba, która nie ukończyła 18 lat, nie powinna tworzyć konta ani korzystać z Aplikacji.',
        'Umowa o świadczenie usługi zostaje zawarta na czas nieokreślony z chwilą utworzenia konta po udostępnieniu użytkownikowi Regulaminu i Polityki prywatności.',
        'Utworzenie konta i korzystanie z Aplikacji wymaga akceptacji Regulaminu oraz zapoznania się z Polityką prywatności. Brak akceptacji uniemożliwia utworzenie konta.',
        'Aplikacja nie udostępnia trybu gościa. Konto jest wymagane w celu kontroli dostępu do chronionych materiałów, przywracania dostępu oraz zapisywania i odtwarzania kopii danych nauki powiązanych z kontem.',
      ],
    },
    {
      title: 'Zakres usługi',
      paragraphs: [
        'SprintSpeak udostępnia darmowe lekcje języka angielskiego dla branży IT, w szczególności dialogi do odsłuchania, teksty, tłumaczenia, słownictwo oraz materiały wspierające rozumienie i powtarzanie wypowiedzi.',
        'W ramach Aplikacji użytkownik może w szczególności:',
      ],
      items: [
        'przeglądać lekcje, rozdziały, tematy i dialogi na dostępnych poziomach trudności,',
        'odtwarzać nagrania audio i korzystać z tłumaczeń oraz objaśnień słownictwa,',
        'pobierać wybrane treści, nagrania i grafiki do pamięci urządzenia w celu korzystania offline,',
        'zapisywać na urządzeniu ukończone lekcje, postęp, ulubione materiały, słówka, ustawienia i historię nauki; przy wylogowaniu słownik, ulubione oraz progres są przesyłane na serwer jako kopia powiązana z kontem,',
        'korzystać z jednego konta na wielu obsługiwanych urządzeniach.',
      ],
      footer: [
        'Usługa jest obecnie całkowicie nieodpłatna i nie zawiera płatności, zakupów w aplikacji ani subskrypcji.',
        'Aplikacja obecnie nie wyświetla reklam. Wprowadzenie reklam, płatności lub płatnych funkcji będzie wymagało wcześniejszego przekazania odpowiednich informacji użytkownikom oraz aktualizacji właściwych dokumentów i deklaracji sklepowych.',
      ],
    },
    {
      title: 'Wymagania techniczne',
      paragraphs: ['Do prawidłowego korzystania z Aplikacji potrzebne są:'],
      items: [
        'obsługiwane urządzenie mobilne albo urządzenie z aktualną wersją wspieranej przeglądarki internetowej,',
        'połączenie z internetem podczas rejestracji, logowania, pobierania nowych materiałów i komunikacji z API,',
        'włączona obsługa JavaScriptu oraz możliwość korzystania z pamięci lokalnej urządzenia, w tym IndexedDB, localStorage lub równoważnych mechanizmów systemowych,',
        'wystarczająca ilość wolnego miejsca na pobrane pliki audio, grafiki, treści i dane postępu.',
      ],
      footer: [
        'Niektóre funkcje mogą działać odmiennie zależnie od systemu operacyjnego, wersji Aplikacji, przeglądarki, ustawień urządzenia i ograniczeń producenta urządzenia.',
        'Użytkownik ponosi koszty połączenia z internetem zgodnie z umową zawartą ze swoim operatorem.',
      ],
    },
    {
      title: 'Konto i logowanie',
      items: [
        'Logowanie odbywa się bez hasła. Użytkownik podaje adres e-mail, na który wysyłany jest jednorazowy kod weryfikacyjny.',
        'Kod jest ważny przez 5 minut i może zostać wykorzystany tylko raz.',
        'Ze względów bezpieczeństwa możliwość wysłania kolejnego kodu jest ograniczona co najmniej do jednego kodu na 10 minut. Usługodawca może stosować dodatkowe limity prób logowania i zabezpieczenia przed nadużyciami.',
        'Użytkownik powinien podać własny, prawidłowy adres e-mail, do którego ma dostęp.',
        'Użytkownik nie może przekazywać kodu, tokenu dostępu ani konta osobom trzecim oraz powinien zabezpieczyć urządzenie przed nieuprawnionym dostępem.',
        'W razie podejrzenia nieuprawnionego użycia konta użytkownik powinien niezwłocznie skontaktować się z Usługodawcą.',
      ],
    },
    {
      title: 'Prawa autorskie i licencja',
      paragraphs: [
        'Aplikacja, jej kod, układ, nazwa, oznaczenia, dialogi, teksty, nagrania, tłumaczenia, grafiki, ćwiczenia i pozostałe materiały są chronione prawem autorskim oraz innymi prawami własności intelektualnej. Prawa te przysługują Wirebase Sp. z o.o. albo jej licencjodawcom.',
        'Usługodawca udziela użytkownikowi nieodpłatnej, niewyłącznej, niezbywalnej, nieprzenoszalnej, niepodlegającej sublicencjonowaniu i odwołalnej licencji na korzystanie z Aplikacji oraz materiałów wyłącznie na własny użytek osobisty i edukacyjny, przez okres posiadania aktywnego konta.',
        'Licencja pozwala na odtwarzanie materiałów w Aplikacji oraz tworzenie lokalnych kopii technicznych wyłącznie za pomocą funkcji udostępnionych przez Aplikację.',
        'Użytkownik nie nabywa praw autorskich ani innych praw własności intelektualnej do Aplikacji lub materiałów.',
      ],
    },
    {
      title: 'Zasady korzystania i działania zabronione',
      paragraphs: [
        'Użytkownik zobowiązuje się korzystać z Aplikacji zgodnie z prawem, Regulaminem, przeznaczeniem usługi i dobrymi obyczajami.',
        'Zabronione jest w szczególności:',
      ],
      items: [
        'kopiowanie, utrwalanie, publikowanie lub rozpowszechnianie tekstów, nagrań, tłumaczeń, grafik i innych materiałów poza zakresem dozwolonym Regulaminem i przepisami prawa,',
        'wyodrębnianie plików audio lub innych materiałów oraz pobieranie ich poza funkcjami udostępnionymi w Aplikacji,',
        'udostępnianie konta, kodów logowania lub tokenów dostępu innym osobom,',
        'sprzedaż, odsprzedaż, odpłatne udostępnianie albo komercyjne wykorzystywanie dostępu lub materiałów,',
        'automatyczne pobieranie danych, scraping, masowe odpytywanie API, indeksowanie lub tworzenie własnych zbiorów danych bez uprzedniej pisemnej zgody Usługodawcy,',
        'obchodzenie zabezpieczeń, usuwanie oznaczeń prawnych, ingerowanie w kod lub podejmowanie prób uzyskania nieuprawnionego dostępu,',
        'wykorzystywanie materiałów do trenowania modeli, tworzenia konkurencyjnych produktów lub baz danych bez zgody Usługodawcy,',
        'działanie mogące zakłócić pracę Aplikacji, API, serwerów lub infrastruktury.',
      ],
    },
    {
      title: 'Dane lokalne, kopia danych nauki i korzystanie offline',
      paragraphs: [
        'Aplikacja może zapisywać na urządzeniu użytkownika pobrane pliki audio, grafiki i treści, ukończone lekcje, postęp, ulubione materiały, zapisane słówka, ustawienia i historię nauki.',
        'Przy wylogowaniu Aplikacja wysyła na serwer kopię słownika użytkownika, ulubionych materiałów oraz progresu nauki. Kopia jest przypisana do konta i służy wyłącznie do zachowania oraz późniejszego przywrócenia tych danych.',
        'Przy kolejnym zalogowaniu Aplikacja pobiera z serwera zapisaną kopię słownika, ulubionych materiałów i progresu oraz odtwarza ją na urządzeniu użytkownika.',
        'Pobrane pliki audio, grafiki i pozostałe materiały offline nie są częścią tej kopii i pozostają lokalnie na urządzeniu.',
        'Usunięcie konta powoduje trwałe usunięcie z serwera danych konta oraz zapisanej kopii słownika, ulubionych materiałów i progresu. Dane te nie są zachowywane po usunięciu konta.',
        'Usunięcie konta z poziomu aplikacji mobilnej usuwa również dane lokalne zapisane w tej instalacji Aplikacji.',
        'Jeżeli konto zostanie usunięte poza konkretnym urządzeniem, np. przez stronę internetową lub e-mail, dane pozostające lokalnie na innych urządzeniach mogą wymagać usunięcia przez wyczyszczenie danych Aplikacji lub jej odinstalowanie.',
      ],
    },
    {
      title: 'Komunikacja z użytkownikiem',
      paragraphs: [
        'Usługodawca może wysyłać na adres e-mail użytkownika wyłącznie wiadomości niezbędne do świadczenia usługi, w szczególności kody logowania, komunikaty bezpieczeństwa, informacje dotyczące konta, awarii, istotnych zmian Aplikacji, Regulaminu lub Polityki prywatności oraz zakończenia świadczenia usługi.',
        'Usługodawca nie wysyła newsletterów, promocji, informacji handlowych ani marketingowych przypomnień o nauce.',
        'Aplikacja obecnie nie wysyła powiadomień push. Funkcja może zostać wprowadzona w przyszłości po aktualizacji dokumentów, właściwych deklaracji sklepowych i uzyskaniu wymaganej zgody systemowej użytkownika.',
      ],
    },
    {
      title: 'Dostępność, aktualizacje i bezpieczeństwo',
      paragraphs: [
        'Usługodawca może rozwijać Aplikację, poprawiać błędy, aktualizować treści, zmieniać sposób działania funkcji oraz czasowo ograniczać dostęp w związku z pracami technicznymi, aktualizacjami lub bezpieczeństwem.',
        'Usługodawca może dodać, zmienić albo wycofać część materiałów, jeżeli nie pozbawia to konsumenta praw wynikających z bezwzględnie obowiązujących przepisów.',
        'Usługodawca nie gwarantuje nieprzerwanej i bezbłędnej dostępności Aplikacji, ale podejmuje rozsądne działania w celu utrzymania jej działania i bezpieczeństwa.',
        'Usługodawca może czasowo zawiesić albo ograniczyć dostęp do konta w przypadku naruszenia Regulaminu, podejrzenia nadużycia, zagrożenia bezpieczeństwa lub konieczności ochrony infrastruktury.',
        'Jeżeli pozwalają na to względy bezpieczeństwa i przepisy prawa, użytkownik zostanie poinformowany o przyczynie ograniczenia oraz możliwości wyjaśnienia sytuacji.',
      ],
    },
    {
      title: 'Usunięcie konta i rozwiązanie umowy',
      paragraphs: [
        'Użytkownik może w każdej chwili, bez opłat, rozwiązać umowę i usunąć konto za pomocą funkcji „Usuń konto” dostępnej w ustawieniach Aplikacji.',
        `Żądanie usunięcia konta można również złożyć pod adresem ${LEGAL_META.urls.deleteAccount} albo wysłać na ${mail()}.`,
        'Usunięcie konta z poziomu Aplikacji jest wykonywane automatycznie i niezwłocznie. Usunięcie jest nieodwracalne i powoduje utratę dostępu do konta.',
        'Usunięcie konta powoduje trwałe usunięcie z serwera wszystkich danych powiązanych z kontem, w tym adresu e-mail, danych uwierzytelniających oraz zapisanej kopii słownika, ulubionych materiałów i progresu. Dane te nie są zachowywane w celu późniejszego odtworzenia konta.',
        'Usługodawca może rozwiązać umowę z ważnej przyczyny, w szczególności w przypadku poważnego lub powtarzającego się naruszania Regulaminu, zagrożenia bezpieczeństwa albo zakończenia świadczenia usługi. Jeżeli jest to możliwe, użytkownik zostanie wcześniej poinformowany.',
      ],
    },
    {
      title: 'Treści edukacyjne i odpowiedzialność',
      paragraphs: [
        'Aplikacja jest narzędziem edukacyjnym. Materiały mogą zawierać uproszczenia, warianty językowe lub sporadyczne błędy.',
        'Aplikacja nie zastępuje nauczyciela języka, profesjonalnego tłumacza, tłumacza przysięgłego ani specjalistycznego doradztwa.',
        'Usługodawca nie gwarantuje osiągnięcia określonego poziomu językowego, wyniku egzaminu, rozmowy rekrutacyjnej ani innych rezultatów nauki.',
        'Usługodawca odpowiada za niewykonanie lub nienależyte wykonanie umowy na zasadach wynikających z obowiązujących przepisów.',
        'Usługodawca nie odpowiada za problemy wynikające wyłącznie z urządzenia użytkownika, braku połączenia z internetem, nieaktualnego oprogramowania, działania operatora telekomunikacyjnego, nieautoryzowanej modyfikacji Aplikacji lub naruszenia Regulaminu przez użytkownika.',
        'Żadne postanowienie Regulaminu nie wyłącza ani nie ogranicza praw konsumenta, których nie można wyłączyć lub ograniczyć na podstawie bezwzględnie obowiązujących przepisów.',
      ],
    },
    {
      title: 'Reklamacje i kontakt',
      paragraphs: [
        `Reklamacje dotyczące działania Aplikacji można przesyłać na ${mail()}. Zgłoszenie powinno zawierać opis problemu, datę jego wystąpienia oraz informacje pozwalające zidentyfikować konto, jeżeli jest to konieczne do obsługi sprawy.`,
        'Usługodawca odpowiada na reklamację bez zbędnej zwłoki, nie później niż w terminie 14 dni od jej otrzymania.',
        'Jeżeli rozpatrzenie reklamacji wymaga dodatkowych informacji, Usługodawca może poprosić użytkownika o ich uzupełnienie.',
      ],
    },
    {
      title: 'Dystrybucja przez sklepy z aplikacjami',
      paragraphs: [
        'Aplikacja może być dystrybuowana za pośrednictwem Google Play i Apple App Store. Operator sklepu nie jest Usługodawcą i nie odpowiada za świadczenie usługi opisanej w Regulaminie.',
        'Pobieranie Aplikacji, aktualizacje i korzystanie z konta sklepowego podlegają także zasadom właściwego operatora sklepu.',
      ],
    },
    {
      title: 'Zmiany Regulaminu',
      paragraphs: [
        'Regulamin może zostać zmieniony z ważnych przyczyn, w szczególności w razie zmiany prawa, wymagań sklepów z aplikacjami, danych Usługodawcy, zakresu lub sposobu działania usługi, technologii albo środków bezpieczeństwa.',
        'O zmianach wpływających na prawa lub obowiązki użytkownika Usługodawca poinformuje w Aplikacji, a w przypadku zmian istotnych również e-mailem, z odpowiednim wyprzedzeniem, chyba że natychmiastowa zmiana jest wymagana przez prawo lub bezpieczeństwo.',
        'Jeżeli użytkownik nie akceptuje nowej wersji Regulaminu, może przed jej wejściem w życie zaprzestać korzystania z usługi i usunąć konto.',
        'Aplikacja zapisuje numer wersji Regulaminu oraz datę jego zaakceptowania przez użytkownika.',
      ],
    },
    {
      title: 'Postanowienia końcowe',
      paragraphs: [
        'Do umowy stosuje się prawo polskie.',
        'Spory są rozstrzygane przez sąd właściwy zgodnie z obowiązującymi przepisami. Postanowienie to nie ogranicza praw konsumenta ani możliwości korzystania z dostępnych pozasądowych sposobów rozpatrywania reklamacji i dochodzenia roszczeń.',
        `Kontakt we wszystkich sprawach związanych z Aplikacją: ${mail()}.`,
      ],
    },
  ],
};

/** Polityka prywatności. */
export const PRIVACY = {
  title: 'Polityka prywatności',
  lead: 'Informacje o danych przetwarzanych w związku z korzystaniem z aplikacji SprintSpeak.',
  version: LEGAL_META.version,
  effectiveDate: LEGAL_META.effectiveDate,
  lastUpdated: LEGAL_META.lastUpdated,
  sections: [
    {
      title: 'Administrator danych i kontakt',
      paragraphs: [
        `Administratorem danych osobowych użytkowników Aplikacji ${LEGAL_META.appName} jest ${operatorName()} („Administrator”).`,
        `W sprawach dotyczących prywatności, danych osobowych, realizacji praw, pomocy technicznej i reklamacji można skontaktować się z Administratorem pod adresem ${mail()} lub przez stronę ${LEGAL_META.urls.contact}.`,
        'Administrator nie wyznaczył inspektora ochrony danych.',
        `Aktualna Polityka prywatności jest dostępna w Aplikacji oraz pod adresem ${LEGAL_META.urls.privacy}.`,
      ],
    },
    {
      title: 'Zakres Polityki',
      paragraphs: [
        'Polityka dotyczy danych przetwarzanych przez Administratora w związku z korzystaniem z aplikacji mobilnej, wersji przeglądarkowej PWA, API, formularza usunięcia konta oraz obsługi kontaktu i reklamacji.',
        'Operatorzy Google Play i Apple App Store mogą niezależnie przetwarzać dane związane z pobraniem Aplikacji, kontem sklepowym, urządzeniem i działaniem sklepu. W tym zakresie stosuje się ich własne zasady prywatności.',
      ],
    },
    {
      title: 'Jakie dane przetwarzamy',
      paragraphs: [
        'W zależności od sposobu korzystania z Aplikacji możemy przetwarzać następujące kategorie danych:',
      ],
      items: [
        'dane konta — adres e-mail, wewnętrzny identyfikator użytkownika, data utworzenia konta, data ostatniego logowania, status konta lub blokady oraz numer i data zaakceptowanej wersji Regulaminu,',
        'dane uwierzytelniające — jednorazowy kod logowania, informacja o jego ważności i wykorzystaniu oraz token dostępu lub sesji,',
        'dane techniczne przekazywane podczas korzystania z API — typ urządzenia, User-Agent, wersja Aplikacji oraz informacje niezbędne do obsługi żądania,',
        'kopia danych nauki — zapisany słownik użytkownika, ulubione materiały oraz progres nauki przesyłane na serwer w momencie wylogowania i przypisane do konta w celu ich późniejszego przywrócenia,',
        'standardowe logi serwera WWW i API — adres IP, data i godzina żądania, wywołany adres lub endpoint, metoda żądania, kod odpowiedzi HTTP, User-Agent oraz informacje o błędzie technicznym,',
        'dane korespondencji — adres e-mail, treść wiadomości oraz dane urządzenia przekazane w zgłoszeniu pomocy lub reklamacji,',
        'dane związane z usunięciem konta — adres e-mail, data żądania i informacje potrzebne do potwierdzenia uprawnienia do konta oraz wykonania żądania.',
      ],
      footer: [
        'Standardowe logi dostępu nie zawierają adresu e-mail użytkownika ani treści przesyłanych żądań.',
        'Nie zbieramy imienia, nazwiska, pseudonimu, zdjęcia, wieku, stanowiska, dokładnej lokalizacji, kontaktów, danych płatniczych, danych zdrowotnych ani nagrań głosu.',
        'Podanie adresu e-mail jest dobrowolne, ale niezbędne do utworzenia konta, logowania oraz przywrócenia dostępu.',
      ],
    },
    {
      title: 'Dane lokalne i kopia danych nauki na serwerze',
      paragraphs: [
        'W celu działania offline i zapamiętywania stanu Aplikacja zapisuje część informacji w pamięci urządzenia, przeglądarki lub aplikacji natywnej.',
      ],
      items: [
        'pobrane pliki audio, grafiki, lekcje, dialogi, tłumaczenia i pozostałe treści,',
        'ukończone lekcje i progres nauki,',
        'ulubione materiały i zapisane słówka,',
        'ustawienia Aplikacji i historia nauki,',
        'techniczne dane sesji niezbędne do utrzymania zalogowania.',
      ],
      footer: [
        'W momencie wylogowania słownik użytkownika, ulubione materiały oraz progres nauki są przesyłane na serwer jako kopia powiązana z kontem.',
        'Przy kolejnym zalogowaniu kopia jest pobierana z serwera i służy do przywrócenia słownika, ulubionych materiałów oraz progresu na urządzeniu.',
        'Pobrane pliki audio, grafiki i pozostałe materiały offline nie są wysyłane na serwer w ramach tej kopii.',
        'Usunięcie konta powoduje trwałe usunięcie z serwera zarówno danych konta, jak i zapisanej kopii słownika, ulubionych materiałów oraz progresu.',
        'Usunięcie konta z poziomu aplikacji mobilnej usuwa również dane lokalne zapisane w tej instalacji. Jeżeli konto zostanie usunięte poza urządzeniem, dane lokalne na innych urządzeniach mogą wymagać usunięcia przez wyczyszczenie danych Aplikacji lub jej odinstalowanie.',
      ],
    },
    {
      title: 'Skąd pochodzą dane',
      items: [
        'bezpośrednio od użytkownika — gdy podaje adres e-mail, kontaktuje się z Administratorem lub składa żądanie usunięcia konta,',
        'z Aplikacji i urządzenia użytkownika — gdy Aplikacja łączy się z API, pobiera materiały oraz w momencie wylogowania przesyła kopię słownika, ulubionych materiałów i progresu,',
        'od dostawcy poczty transakcyjnej — w zakresie technicznych informacji o wysłaniu i dostarczeniu wiadomości z kodem logowania.',
      ],
    },
    {
      title: 'Cele i podstawy prawne przetwarzania',
      paragraphs: ['Dane przetwarzamy w następujących celach:'],
      items: [
        'utworzenie i obsługa konta, kontrola dostępu do materiałów, logowanie, utrzymanie sesji, przywracanie dostępu oraz zapisywanie i odtwarzanie kopii słownika, ulubionych materiałów i progresu — art. 6 ust. 1 lit. b RODO, czyli wykonanie umowy,',
        'wysyłanie jednorazowych kodów logowania i niezbędnych komunikatów dotyczących konta lub działania usługi — art. 6 ust. 1 lit. b RODO,',
        'zapewnienie bezpieczeństwa Aplikacji, ograniczanie prób logowania, wykrywanie nadużyć, diagnozowanie błędów, ochrona infrastruktury oraz ustalanie, dochodzenie lub obrona roszczeń — art. 6 ust. 1 lit. f RODO, czyli prawnie uzasadniony interes Administratora,',
        'obsługa pytań, pomocy technicznej i reklamacji — art. 6 ust. 1 lit. b RODO, a w zakresie dokumentowania spraw i ochrony przed roszczeniami również art. 6 ust. 1 lit. f RODO,',
        'realizacja żądania usunięcia konta i innych praw użytkownika — art. 6 ust. 1 lit. c RODO oraz art. 6 ust. 1 lit. f RODO w zakresie zabezpieczenia przed nieuprawnionym usunięciem danych,',
        'realizacja obowiązków wynikających z przepisów prawa oraz odpowiedź na prawnie wiążące żądania uprawnionych organów — art. 6 ust. 1 lit. c RODO.',
      ],
      footer: [
        'Nie przetwarzamy danych w celu przesyłania promocji, newsletterów, marketingowych przypomnień o nauce, reklamy behawioralnej ani profilowania.',
      ],
    },
    {
      title: 'Wiadomości e-mail',
      paragraphs: [
        'Na adres e-mail użytkownika mogą być wysyłane jedynie wiadomości niezbędne do świadczenia usługi: kody logowania, komunikaty bezpieczeństwa, informacje dotyczące konta, awarii, istotnych zmian Aplikacji, Regulaminu lub Polityki prywatności oraz zakończenia świadczenia usługi.',
        'Administrator nie wysyła wiadomości promocyjnych ani informacji handlowych.',
      ],
    },
    {
      title: 'Pliki cookie, analityka, reklamy i powiadomienia',
      paragraphs: [
        'Aplikacja nie korzysta obecnie z zewnętrznych narzędzi analitycznych, reklamowych ani SDK służących do śledzenia lub profilowania użytkowników.',
        'Aplikacja nie wyświetla obecnie reklam i nie przekazuje danych partnerom reklamowym ani brokerom danych.',
        'Aplikacja nie wysyła obecnie powiadomień push i nie zapisuje tokenów urządzeń do tego celu.',
        'Mechanizmy pamięci lokalnej są używane do logowania, działania offline, przechowywania pobranych materiałów i zapisywania ustawień lub postępu.',
        'Przed wprowadzeniem analityki, reklam albo powiadomień push Administrator zaktualizuje Politykę prywatności i deklaracje wymagane przez właściwy sklep z aplikacjami oraz uzyska wymagane zgody lub uprawnienia.',
      ],
    },
    {
      title: 'Odbiorcy danych',
      paragraphs: [
        'Dane mogą być udostępniane wyłącznie w niezbędnym zakresie następującym kategoriom odbiorców:',
      ],
      items: [
        'dostawcom hostingu, infrastruktury serwerowej, baz danych i usług sieciowych,',
        'dostawcom przechowywania oraz dystrybucji plików audio, grafik i pozostałych materiałów,',
        'dostawcy poczty transakcyjnej wykorzystywanemu do wysyłania kodów logowania oraz technicznych wiadomości dotyczących usługi,',
        'dostawcom wsparcia technicznego, bezpieczeństwa, obsługi prawnej lub audytowej, jeżeli jest to potrzebne do obsługi konkretnej sprawy,',
        'organom publicznym lub innym uprawnionym podmiotom, jeżeli obowiązek udostępnienia wynika z prawa lub prawnie wiążącego żądania.',
      ],
      footer: [
        'Podmioty przetwarzające działają na podstawie umów i mogą przetwarzać dane wyłącznie zgodnie z poleceniami Administratora oraz w zakresie niezbędnym do świadczenia powierzonych usług.',
        'Nie sprzedajemy danych osobowych i nie udostępniamy ich do celów marketingowych lub reklamowych.',
      ],
    },
    {
      title: 'Przekazywanie danych poza Europejski Obszar Gospodarczy',
      paragraphs: [
        'Administrator korzysta przede wszystkim z dostawców przetwarzających dane w Europejskim Obszarze Gospodarczym.',
        'Jeżeli dostawca lub jego podwykonawca przetwarza dane poza EOG, przekazanie odbywa się wyłącznie na podstawie mechanizmu dopuszczonego przez RODO, w szczególności decyzji Komisji Europejskiej stwierdzającej odpowiedni stopień ochrony, standardowych klauzul umownych albo innej właściwej podstawy prawnej, wraz z dodatkowymi zabezpieczeniami, gdy są wymagane.',
      ],
    },
    {
      title: 'Okres przechowywania danych',
      items: [
        'adres e-mail, identyfikator konta, daty utworzenia i logowania, status konta oraz informacja o zaakceptowanej wersji Regulaminu — do czasu usunięcia konta lub innego zakończenia umowy,',
        'kopia słownika, ulubionych materiałów i progresu — do czasu jej zastąpienia nowszą kopią przy kolejnym wylogowaniu albo do usunięcia konta; usunięcie konta powoduje trwałe usunięcie tej kopii z serwera,',
        'jednorazowy kod logowania — maksymalnie przez 5 minut, do chwili jego wykorzystania albo wcześniejszego unieważnienia,',
        'token dostępu lub sesji — do czasu wygaśnięcia, wylogowania, unieważnienia albo usunięcia konta,',
        'standardowe logi serwera WWW, API i bezpieczeństwa — przez 30 dni, chyba że konkretny zapis jest potrzebny dłużej do wyjaśnienia incydentu, przeciwdziałania nadużyciu albo dochodzenia lub obrony roszczeń,',
        'korespondencja dotycząca pomocy technicznej i reklamacji — przez 1 rok od zakończenia sprawy,',
        'dane związane z realizacją praw użytkownika, w tym żądaniem usunięcia konta — przez okres niezbędny do realizacji żądania i wykazania jego wykonania, nie dłużej niż 1 rok, chyba że dłuższy okres wynika z prawa lub jest konieczny do ochrony przed roszczeniami,',
        'dane zapisane lokalnie na urządzeniu — do czasu ich usunięcia przez użytkownika, usunięcia danych Aplikacji, odinstalowania Aplikacji albo usunięcia przez system operacyjny.',
      ],
      footer: [
        'Po usunięciu konta dane konta oraz serwerowa kopia słownika, ulubionych materiałów i progresu są trwale usuwane i nie są przechowywane w celu późniejszego odtworzenia konta.',
        'Techniczne dane wysyłki wiadomości mogą być dodatkowo przetwarzane przez dostawcę poczty przez okres wynikający z zawartej umowy, jego obowiązków prawnych oraz ustawień usługi.',
      ],
    },
    {
      title: 'Usunięcie konta i danych',
      paragraphs: [
        'Konto można usunąć bezpośrednio w ustawieniach Aplikacji za pomocą funkcji „Usuń konto”. Usunięcie z poziomu Aplikacji jest automatyczne i wykonywane niezwłocznie.',
        `Żądanie usunięcia konta można również złożyć pod adresem ${LEGAL_META.urls.deleteAccount} albo wysłać na ${mail()}. W takim przypadku Administrator może zweryfikować, czy żądanie pochodzi od osoby posiadającej dostęp do danego adresu e-mail.`,
        'Po usunięciu konta usuwamy adres e-mail, identyfikator konta, dane logowania, status konta oraz serwerową kopię słownika, ulubionych materiałów i progresu.',
        'Usunięcie danych konta i kopii danych nauki jest trwałe — Administrator nie zachowuje ich w celu późniejszego przywrócenia usuniętego konta.',
        'Usunięcie konta z poziomu aplikacji mobilnej usuwa również lokalne dane z tej instalacji. W przypadku usunięcia przez stronę internetową lub e-mail dane lokalne pozostające na urządzeniach użytkownik usuwa przez wyczyszczenie danych Aplikacji lub jej odinstalowanie.',
        'Standardowe logi techniczne, które nie zawierają adresu e-mail ani treści żądań i nie stanowią kopii konta użytkownika, podlegają niezależnej 30-dniowej retencji opisanej w niniejszej Polityce. Korespondencja dotycząca pomocy technicznej lub reklamacji podlega odrębnej retencji wskazanej powyżej.',
      ],
    },
    {
      title: 'Prawa użytkownika',
      paragraphs: ['Na zasadach określonych w RODO użytkownikowi przysługuje prawo do:'],
      items: [
        'dostępu do danych i otrzymania ich kopii,',
        'sprostowania nieprawidłowych danych lub uzupełnienia danych niekompletnych,',
        'usunięcia danych,',
        'ograniczenia przetwarzania,',
        'przeniesienia danych, jeżeli przetwarzanie odbywa się automatycznie na podstawie umowy lub zgody,',
        'wniesienia sprzeciwu wobec przetwarzania opartego na prawnie uzasadnionym interesie Administratora, z przyczyn związanych ze szczególną sytuacją użytkownika,',
        'wycofania zgody w dowolnym momencie, jeżeli określone przetwarzanie byłoby oparte na zgodzie; wycofanie nie wpływa na zgodność z prawem wcześniejszego przetwarzania.',
      ],
      footer: [
        `Żądania można kierować na ${mail()}. Administrator może poprosić o informacje potrzebne do potwierdzenia tożsamości i ochrony konta przed nieuprawnionym ujawnieniem, zmianą lub usunięciem danych.`,
        'Administrator odpowiada bez zbędnej zwłoki, co do zasady w ciągu miesiąca. W przypadkach przewidzianych w RODO termin może zostać przedłużony, o czym użytkownik zostanie poinformowany wraz z podaniem przyczyny.',
        'Użytkownik ma prawo wnieść skargę do Prezesa Urzędu Ochrony Danych Osobowych, ul. Stanisława Moniuszki 1A, 00-014 Warszawa.',
      ],
    },
    {
      title: 'Zautomatyzowane decyzje i profilowanie',
      paragraphs: [
        'Administrator nie podejmuje wobec użytkowników decyzji wywołujących skutki prawne lub w podobny sposób istotnie na nich wpływających wyłącznie w sposób zautomatyzowany.',
        'Administrator nie profiluje użytkowników i nie wykorzystuje danych do reklamy behawioralnej.',
      ],
    },
    {
      title: 'Bezpieczeństwo danych',
      paragraphs: [
        'Administrator stosuje środki techniczne i organizacyjne odpowiednie do ryzyka, w tym szyfrowane połączenia HTTPS, ograniczony dostęp administracyjny, zabezpieczenia infrastruktury, szyfrowanie zapewniane na poziomie infrastruktury, rejestrowanie zdarzeń technicznych, okresowe aktualizacje oprogramowania oraz ograniczenie zakresu zbieranych danych.',
        'Logowanie jest bezhasłowe. Kod logowania jest ważny przez 5 minut, jednorazowy, a możliwość wysyłania kodów i wykonywania prób logowania jest ograniczana w celu przeciwdziałania nadużyciom.',
        'Żadna metoda transmisji lub przechowywania danych nie zapewnia całkowitego bezpieczeństwa. W przypadku podejrzenia naruszenia ochrony danych należy niezwłocznie skontaktować się z Administratorem.',
      ],
    },
    {
      title: 'Osoby niepełnoletnie',
      paragraphs: [
        'Aplikacja jest przeznaczona wyłącznie dla osób, które ukończyły 18 lat, i nie jest kierowana do dzieci ani młodzieży.',
        'Aplikacja nie prowadzi technicznej weryfikacji wieku. Jeżeli Administrator dowie się, że konto utworzyła osoba niepełnoletnia, może zablokować lub usunąć konto oraz związane z nim dane.',
      ],
    },
    {
      title: 'Zmiany Polityki prywatności',
      paragraphs: [
        'Polityka może być aktualizowana w przypadku zmiany prawa, wymagań sklepów z aplikacjami, dostawców, funkcji Aplikacji, środków bezpieczeństwa lub sposobu przetwarzania danych.',
        'Aktualna wersja jest dostępna w Aplikacji i na publicznej stronie internetowej. O istotnych zmianach Administrator poinformuje w Aplikacji, a gdy będzie to potrzebne także e-mailem, przed rozpoczęciem nowego sposobu przetwarzania.',
      ],
    },
    {
      title: 'Kontakt',
      paragraphs: [
        `Pytania dotyczące Polityki prywatności, danych osobowych, usunięcia konta albo realizacji praw należy kierować na ${mail()}.`,
      ],
    },
  ],
};

export default {
  meta: LEGAL_META,
  terms: TERMS,
  privacy: PRIVACY,
};
